import type { Invoice } from '@repo/api-client/dtos';
import { displayWireMoney } from '@/features/money';

export type AiSuggestionContent = {
  icon: 'bell' | 'send' | 'sparkle' | 'mail';
  title: string;
  body: string;
  actionLabel: string;
  prompt: string;
};

const MAX_INLINE_OVERDUE = 5;

function daysLate(invoice: Invoice, now = Date.now()): number {
  if (invoice.status !== 'overdue') return 0;
  const due = new Date(invoice.dueDate).getTime();
  if (!Number.isFinite(due)) return 0;
  return Math.max(0, Math.floor((now - due) / (24 * 60 * 60 * 1000)));
}

export function pickDashboardSuggestion(args: {
  invoices: readonly Invoice[];
  now?: number;
}): AiSuggestionContent {
  const now = args.now ?? Date.now();
  const overdue = args.invoices.filter((i) => i.status === 'overdue');
  if (overdue.length > 0) {
    return overdueSuggestion(overdue, now);
  }

  const drafts = args.invoices.filter((i) => i.status === 'draft');
  if (drafts.length > 0) {
    return {
      icon: 'send',
      title: `${drafts.length} draft${drafts.length === 1 ? '' : 's'} waiting. Ready to finish ${drafts.length === 1 ? 'it' : 'them'}?`,
      body: "Tell me what changed and I'll polish each one for you to send.",
      actionLabel: 'Finish drafts',
      prompt: `I have ${drafts.length} draft invoice${drafts.length === 1 ? '' : 's'} waiting to be finalised. Help me work through ${drafts.length === 1 ? 'it' : 'them'}: ask me what needs changing on each one.`,
    };
  }

  if (args.invoices.length === 0) {
    return {
      icon: 'sparkle',
      title: 'Draft your first invoice with one sentence',
      body: `Try "Tuesday's job for the Hendersons, water heater install, $1,840" and I'll do the rest.`,
      actionLabel: 'Try it',
      prompt: 'Draft an invoice for a recent job. Ask me what was done, who for, and how much.',
    };
  }

  return {
    icon: 'sparkle',
    title: 'Need a quick invoice?',
    body: "Describe a job in one line and I'll have it ready for you to review.",
    actionLabel: 'Ask Invoyss',
    prompt: 'Help me draft a new invoice. Ask me about the work, the client, and the amount.',
  };
}

function overdueSuggestion(
  overdue: readonly Invoice[],
  now: number,
): AiSuggestionContent {
  const byUrgency = [...overdue].sort(
    (a, b) => daysLate(b, now) - daysLate(a, now),
  );
  const inlined = byUrgency.slice(0, MAX_INLINE_OVERDUE);
  const remainder = byUrgency.length - inlined.length;

  const headline = `${overdue.length} invoice${overdue.length === 1 ? '' : 's'} overdue. Want me to chase?`;
  const body =
    "I'll draft a polite reminder for each, with one-tap pay links. You approve before anything sends.";

  if (overdue.length === 1) {
    const inv = inlined[0]!;
    const amt = displayWireMoney(inv.totals.amountDue, inv.currency);
    return {
      icon: 'bell',
      title: headline,
      body,
      actionLabel: 'Draft reminder',
      prompt: `Draft a reminder for ${inv.invoiceNumber} (${inv.clientNameSnapshot}, ${amt}, ${formatDaysLate(daysLate(inv, now))}).`,
    };
  }

  const lines = inlined
    .map((inv) => {
      const amt = displayWireMoney(inv.totals.amountDue, inv.currency);
      return `- ${inv.invoiceNumber}, ${inv.clientNameSnapshot}, ${amt} (${formatDaysLate(daysLate(inv, now))})`;
    })
    .join('\n');
  const tail =
    remainder > 0
      ? `\n\n(There ${remainder === 1 ? 'is' : 'are'} also ${remainder} more overdue invoice${remainder === 1 ? '' : 's'}. Start with these and we'll get to the rest after.)`
      : '';

  return {
    icon: 'bell',
    title: headline,
    body,
    actionLabel:
      overdue.length > MAX_INLINE_OVERDUE ? `Chase top ${MAX_INLINE_OVERDUE}` : 'Draft reminders',
    prompt: `Draft a reminder for each of these overdue invoices (one per invoice, pick the tone that matches how late it is):\n${lines}${tail}`,
  };
}

function formatDaysLate(daysLate: number): string {
  if (daysLate <= 0) return 'just past due';
  return `${daysLate} day${daysLate === 1 ? '' : 's'} overdue`;
}
